import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { AppLayout } from "@/components/layout/AppLayout";
import Marketplace from "./pages/Marketplace";
import { OfflineBanner } from "./components/OfflineBanner";
import { ThemeProvider } from "next-themes";

const Landing = lazy(() => import("./pages/Landing"));
const UnifiedDashboard = lazy(() => import("./pages/UnifiedDashboard"));
const Campagnes = lazy(() => import("./pages/Campagnes"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Automatisations = lazy(() => import("./pages/Automatisations"));
const WhatsApp = lazy(() => import("./pages/WhatsApp"));
const Telegram = lazy(() => import("./pages/Telegram"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Parametres = lazy(() => import("./pages/Parametres"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CrmPipeline = lazy(() => import("./pages/crm/CrmPipeline"));
const CrmActivites = lazy(() => import("./pages/crm/CrmActivites"));
const BoutiqueProduits = lazy(() => import("./pages/boutique/BoutiqueProduits"));
const BoutiqueCategories = lazy(() => import("./pages/boutique/BoutiqueCategories"));
const BoutiqueCommandes = lazy(() => import("./pages/boutique/BoutiqueCommandes"));
const BoutiqueConfig = lazy(() => import("./pages/boutique/BoutiqueConfig"));
const BoutiqueCoupons = lazy(() => import("./pages/boutique/BoutiqueCoupons"));
const BoutiqueDesign = lazy(() => import("./pages/boutique/BoutiqueDesign"));
const BoutiquePages = lazy(() => import("./pages/boutique/BoutiquePages"));
const BoutiqueSEO = lazy(() => import("./pages/boutique/BoutiqueSEO"));
const BoutiqueClients = lazy(() => import("./pages/boutique/BoutiqueClients"));
const BoutiqueSegmentation = lazy(() => import("./pages/boutique/BoutiqueSegmentation"));
const BoutiqueForecast = lazy(() => import("./pages/boutique/BoutiqueForecast"));
const BoutiqueLoyalty = lazy(() => import("./pages/boutique/BoutiqueLoyalty"));
const BoutiqueReferrals = lazy(() => import("./pages/boutique/BoutiqueReferrals"));
const BoutiqueAbandonedCarts = lazy(() => import("./pages/boutique/BoutiqueAbandonedCarts"));
const BoutiqueFlashSales = lazy(() => import("./pages/boutique/BoutiqueFlashSales"));
const BoutiqueReviews = lazy(() => import("./pages/boutique/BoutiqueReviews"));
const StoreFront = lazy(() => import("./pages/store/StoreFront"));
const ProductDetail = lazy(() => import("./pages/store/ProductDetail"));
const CartCheckout = lazy(() => import("./pages/store/CartCheckout"));
const StorePage = lazy(() => import("./pages/store/StorePage"));
const StoreCustomerDashboard = lazy(() => import("./pages/store/StoreCustomerDashboard"));
const Install = lazy(() => import("./pages/Install"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AgentIntegrations = lazy(() => import("./pages/AgentIntegrations"));
const Ads = lazy(() => import("./pages/Ads"));

const queryClient = new QueryClient();

const RouteLoading = () => (
  <div className="min-h-screen aurora-mesh flex items-center justify-center" role="status" aria-label="Chargement de la page">
    <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const ProtectedRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen aurora-mesh flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<UnifiedDashboard />} />
        <Route path="/boutique" element={<Navigate to="/app" replace />} />
        <Route path="/boutique/analytics" element={<Navigate to="/app" replace />} />
        <Route path="/boutique/produits" element={<BoutiqueProduits />} />
        <Route path="/boutique/categories" element={<BoutiqueCategories />} />
        <Route path="/boutique/commandes" element={<BoutiqueCommandes />} />
        <Route path="/boutique/clients" element={<BoutiqueClients />} />
        <Route path="/boutique/config" element={<BoutiqueConfig />} />
        <Route path="/boutique/coupons" element={<BoutiqueCoupons />} />
        <Route path="/boutique/seo" element={<BoutiqueSEO />} />
        <Route path="/boutique/design" element={<BoutiqueDesign />} />
        <Route path="/boutique/pages" element={<BoutiquePages />} />
        <Route path="/boutique/segmentation" element={<BoutiqueSegmentation />} />
        <Route path="/boutique/forecast" element={<BoutiqueForecast />} />
        <Route path="/boutique/loyalty" element={<BoutiqueLoyalty />} />
        <Route path="/boutique/referrals" element={<BoutiqueReferrals />} />
        <Route path="/boutique/abandoned-carts" element={<BoutiqueAbandonedCarts />} />
        <Route path="/boutique/flash-sales" element={<BoutiqueFlashSales />} />
        <Route path="/boutique/reviews" element={<BoutiqueReviews />} />
        <Route path="/crm" element={<CrmPipeline />} />
        <Route path="/crm/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/crm/activites" element={<CrmActivites />} />
        <Route path="/campagnes" element={<Campagnes />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/automatisations" element={<Automatisations />} />
        <Route path="/whatsapp" element={<WhatsApp />} />
        <Route path="/telegram" element={<Telegram />} />
        <Route path="/analytics" element={<Navigate to="/app" replace />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/agents" element={<AgentIntegrations />} />
        <Route path="/ads" element={<Ads />} />
        <Route path="/parametres" element={<Parametres />} />
        <Route path="/installer" element={<Install />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="nexus-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <OfflineBanner />
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteLoading />}>
              <Routes>
                <Route path="/" element={<Marketplace />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/store/:slug" element={<StoreFront />} />
                <Route path="/store/:slug/p/:productId" element={<ProductDetail />} />
                <Route path="/store/:slug/panier" element={<CartCheckout />} />
                <Route path="/store/:slug/page/:pageSlug" element={<StorePage />} />
                <Route path="/store/:slug/compte" element={<StoreCustomerDashboard />} />
                <Route path="/app/*" element={<ProtectedRoutes />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
